import memoize from 'lodash/memoize';
import { STORAGE_KEYS } from 'constants.js';

export function setTokenAsCacheKey() {
  return (
    sessionStorage.getItem(STORAGE_KEYS.EMULATION_TOKEN) ??
    sessionStorage.getItem(STORAGE_KEYS.TOKEN) ??
    new Date().getTime()
  );
}

export const setTokenAndParamsAsCacheKey = (params) => {
  const token = sessionStorage.getItem(STORAGE_KEYS.TOKEN) ?? new Date().getTime();
  return `${JSON.stringify(params)}${token}`;
};

export function memoizeWithToken(request) {
  return memoize(request, setTokenAsCacheKey);
}

// Explicitly pass in a unique id to generate a memoization key
export const memoizeWithUidAndParams = (uid) => (fn) =>
  memoize(fn, (params) => `${JSON.stringify(params)}${uid}`);

// Use token from session storage to create a memoization key
export function memoizeWithTokenAndParams(request) {
  return memoize(request, setTokenAndParamsAsCacheKey);
}

export default setTokenAsCacheKey;

// New cache handling for memoize to remove stale data after timeout
class Cache extends memoize.Cache {
  constructor(timeout) {
    super(timeout);
    this.cache = new Map();
    this.ttl = timeout || 30000;
  }

  set(key, value) {
    this.cache.set(key, { value, timestamp: Date.now() });
  }

  get(key) {
    const { timestamp, value } = this.cache.get(key) || {};
    if (value && Date.now() - timestamp < this.ttl) {
      return value;
    }
    // delete it from cache if expired or not found
    this.cache.delete(key);
    return undefined;
  }

  has(key) {
    return this.get(key) !== undefined;
  }
}

// Te cache expires after a set number of milliseconds. This prevents too many API calls from being made
// to help reduce stress on the backend but still allows balances to be updated reasonably frequently
export const memoizeWithExprationTime = (func, timeout = 20000) => {
  const memoized = memoize(func, (...args) => {
    const now = Date.now();
    const key = JSON.stringify([...args, Math.floor(now / timeout)]);

    return key;
  });

  memoized.cache = new Cache(timeout);

  return memoized;
};
